import { useQuery } from '@tanstack/react-query'
import { useStagedActionsQuery, isRecentlyDeleted, type StagedAction } from './use-staged-actions'
import { useAuth } from '@renderer/chat/providers'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import { useMemo, useRef } from 'react'
import {
  isStagedActionDeleted,
  resolveStagedActionSnapshot,
  stagedActionExpandedQueryKey,
  type StagedActionListPresence,
} from './staged-actions/utils'

interface UseStagedActionOptions {
  actionId: string | undefined
}

export function useStagedAction({ actionId }: UseStagedActionOptions) {
  const { getAuthToken } = useAuth()
  const { allActions, hasFetched, isLoading: isLoadingAll } = useStagedActionsQuery()

  // Lean list cache (no expand) — fine for Home rows; detail needs conversation titles.
  const cachedAction = useMemo(() => allActions.find((a) => a.id === actionId) ?? null, [allActions, actionId])

  const {
    data: fetchedAction,
    isLoading: isFetching,
    error,
  } = useQuery({
    queryKey: stagedActionExpandedQueryKey(actionId ?? ''),
    queryFn: async () => {
      const token = await getAuthToken()
      const response = await fetch(getChatApiRoute(`api/v1/staged-actions/${actionId}?expand=true`), {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.status === 404) {
        return null
      }
      if (!response.ok) throw new Error('Failed to fetch action')
      const json = await response.json()
      return json.data as StagedAction
    },
    // Always hydrate expand for the open action — list cache is intentionally lean.
    enabled: !!actionId && !isRecentlyDeleted(actionId),
    staleTime: 5 * 60 * 1000,
    retry: 2,
    placeholderData: () => cachedAction ?? undefined,
  })

  // Whether this action has been in the live list, which is what separates
  // "deleted while open" from "never in the list" (see resolveStagedActionSnapshot).
  // Keyed by id so switching actions doesn't inherit the previous one's history.
  const listPresence = useRef({ actionId, seen: false })
  if (listPresence.current.actionId !== actionId) {
    listPresence.current = { actionId, seen: false }
  }
  if (cachedAction) {
    listPresence.current.seen = true
  }
  const presence: StagedActionListPresence = {
    liveAction: cachedAction,
    liveListLoaded: hasFetched,
    seenInLiveList: listPresence.current.seen,
  }
  const isDeleted = isStagedActionDeleted(presence)

  // The expanded query supplies conversation metadata, while the list query is
  // the live, IPC-synced action source. Combining them keeps an open drawer off
  // a five-minute-old expanded snapshot after an edit, invoke, undo, delete, or
  // update received in another renderer. Memoized because the merge allocates:
  // a fresh action object each render would churn every consumer that keys
  // effects or memos on its identity.
  const action = useMemo(
    () => resolveStagedActionSnapshot(fetchedAction, presence),
    // `presence` is rebuilt every render, so depend on its fields instead.
    [fetchedAction, presence.liveAction, presence.liveListLoaded, presence.seenInLiveList],
  )

  return {
    action,
    // A deleted action resolves immediately — never sit on a skeleton waiting
    // for a read of something that's gone.
    isLoading:
      !isDeleted && ((!!actionId && isFetching && !fetchedAction && !cachedAction) || (isLoadingAll && !cachedAction)),
    error,
  }
}
